/**
 * Sezione "Forniture" nel dettaglio prodotto (Slice B — Forniture a clienti).
 *
 * Mostra la lista delle consegne (deployments) associate al prodotto con:
 *  - toggle "solo correnti" per filtrare le righe is_current
 *  - conteggio finding per release (dal progetto DT della release; "-" se dtProjectUuid assente)
 *  - form "Nuova consegna": seleziona cliente -> siti del cliente -> release -> label/data
 *  - azione "Push release a clienti precedenti" (bulk delivery, mode='all-previous')
 *  - mutazioni visibili solo con permesso PORTFOLIO_MANAGEMENT (come OwnershipSection)
 */

import { useState, type FormEvent } from 'react';
import { usePermissions } from '../../auth/index.js';
import { useCustomers } from '../customers/hooks.js';
import { fetchSites } from '../customers/api.js';
import {
  useDeployments,
  useCreateDeployment,
  useDeleteDeployment,
  useBulkDeliver,
  useReleases,
  useOpenVulnSets,
  usePendingScanSets,
} from './hooks.js';
import type { CreateDeploymentInput, BulkDeliveryInput } from './types.js';
import styles from './Products.module.css';
import { useQuery } from '@tanstack/react-query';
import type { CustomerSite } from '../customers/types.js';
import { PendingScanBadge } from './PendingScanBadge.js';

// ---------------------------------------------------------------------------
// Sotto-componente: conteggio finding per una release
// ---------------------------------------------------------------------------

/**
 * Mostra il numero di finding APERTI per la release indicata (via dtProjectUuid), dallo snapshot
 * più recente (stessa fonte snapshot di lista prodotti / scheda release — niente lettura live da
 * DT). Somma i finding aperti dei bundle della release. "-" se la release non ha dtProjectUuid.
 */
function FindingCount({ dtProjectUuid }: { dtProjectUuid?: string }) {
  const { data: openSets } = useOpenVulnSets();
  const { data: pendingSets } = usePendingScanSets();

  if (!dtProjectUuid) return <span>-</span>;
  const pending = pendingSets?.releaseUuids.has(dtProjectUuid);
  return (
    <span>
      {openSets?.openCountByRelease.get(dtProjectUuid) ?? 0}
      {pending && (
        <>
          {' '}
          <PendingScanBadge testId={`fornitura-pending-${dtProjectUuid}`} />
        </>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componente: form nuova consegna
// ---------------------------------------------------------------------------

function NuovaConsegnaForm({ productId }: { productId: string }) {
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [releaseVersion, setReleaseVersion] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [deliveredAt, setDeliveredAt] = useState('');

  const { data: clienti = [] } = useCustomers();
  const { data: releases = [] } = useReleases(productId);

  // Carica i siti del cliente selezionato
  const { data: siti = [] } = useQuery<CustomerSite[]>({
    queryKey: ['customer-sites-form', customerId],
    queryFn: () => fetchSites(Number(customerId)),
    enabled: typeof customerId === 'number' && customerId > 0,
  });

  const { mutate, isPending, isError, error, reset } = useCreateDeployment(productId);

  function messaggioErrore(): string {
    if (error instanceof Error && error.message) return error.message;
    return 'Errore durante la creazione della consegna.';
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!customerId || !releaseVersion) return;

    const input: CreateDeploymentInput = {
      customerId: Number(customerId),
      siteId: siteId !== '' ? Number(siteId) : undefined,
      releaseVersion,
      label: label.trim() || undefined,
      description: description.trim() || undefined,
      deliveredAt: deliveredAt || undefined,
    };

    mutate(input, {
      onSuccess: () => {
        setCustomerId('');
        setSiteId('');
        setReleaseVersion('');
        setLabel('');
        setDescription('');
        setDeliveredAt('');
        reset();
      },
    });
  }

  return (
    <div className={styles.formCard} data-testid="form-nuova-consegna">
      <p className={styles.formTitle}>Nuova consegna</p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup} style={{ flex: 2 }}>
            <label className={styles.formLabel} htmlFor="dep-customer">
              Cliente *
            </label>
            <select
              id="dep-customer"
              className={styles.formSelect}
              value={customerId}
              onChange={(e) => {
                const v = e.target.value;
                setCustomerId(v === '' ? '' : Number(v));
                setSiteId('');
              }}
              required
              data-testid="select-dep-customer"
            >
              <option value="">Seleziona cliente...</option>
              {clienti.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup} style={{ flex: 2 }}>
            <label className={styles.formLabel} htmlFor="dep-site">
              Sito (opzionale)
            </label>
            <select
              id="dep-site"
              className={styles.formSelect}
              value={siteId}
              onChange={(e) => setSiteId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={!customerId}
              data-testid="select-dep-site"
            >
              <option value="">Nessun sito specifico</option>
              {siti.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup} style={{ flex: 2 }}>
            <label className={styles.formLabel} htmlFor="dep-release">
              Release *
            </label>
            <select
              id="dep-release"
              className={styles.formSelect}
              value={releaseVersion}
              onChange={(e) => setReleaseVersion(e.target.value)}
              required
              data-testid="select-dep-release"
            >
              <option value="">Seleziona release...</option>
              {releases.map((r) => (
                <option key={r.version} value={r.version}>
                  {r.version}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup} style={{ flex: 2 }}>
            <label className={styles.formLabel} htmlFor="dep-label">
              Label / seriale
            </label>
            <input
              id="dep-label"
              type="text"
              className={styles.formInput}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="es. contratto-2024-001"
              data-testid="input-dep-label"
            />
          </div>

          <div className={styles.formGroup} style={{ flex: 1 }}>
            <label className={styles.formLabel} htmlFor="dep-deliveredAt">
              Data consegna
            </label>
            <input
              id="dep-deliveredAt"
              type="date"
              className={styles.formInput}
              value={deliveredAt}
              onChange={(e) => setDeliveredAt(e.target.value)}
              data-testid="input-dep-deliveredAt"
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup} style={{ flex: 1 }}>
            <label className={styles.formLabel} htmlFor="dep-description">
              Descrizione
            </label>
            <input
              id="dep-description"
              type="text"
              className={styles.formInput}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Note libere sulla consegna"
              data-testid="input-dep-description"
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-crea-consegna"
          >
            {isPending ? 'Creazione...' : 'Crea consegna'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-crea-consegna">
              {messaggioErrore()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sotto-componente: form push release a clienti precedenti
// ---------------------------------------------------------------------------

function PushReleaseForm({ productId }: { productId: string }) {
  const { data: releases = [] } = useReleases(productId);
  const [targetVersion, setTargetVersion] = useState('');
  const [previousVersion, setPreviousVersion] = useState('');

  const { mutate, isPending, isError, error, isSuccess } = useBulkDeliver(productId, targetVersion);

  function messaggioErrore(): string {
    if (error instanceof Error && error.message) return error.message;
    return 'Errore durante il push massivo.';
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!targetVersion || !previousVersion) return;

    const input: BulkDeliveryInput = {
      mode: 'all-previous',
      previousVersion,
    };

    // NB: niente reset() qui — azzererebbe isSuccess e nasconderebbe subito il messaggio
    // di esito. Il messaggio "Push completato." resta finché non si rilancia il push.
    mutate(input, {
      onSuccess: () => {
        setPreviousVersion('');
      },
    });
  }

  return (
    <div className={styles.formCard} data-testid="form-push-release">
      <p className={styles.formTitle}>Push release a clienti precedenti</p>
      <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '0.75rem' }}>
        Crea una consegna della release target per tutti i gruppi che hanno attualmente la release
        "precedente" come versione corrente.
      </p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="push-target">
              Release target *
            </label>
            <select
              id="push-target"
              className={styles.formSelect}
              value={targetVersion}
              onChange={(e) => setTargetVersion(e.target.value)}
              required
              data-testid="select-push-target"
            >
              <option value="">Seleziona...</option>
              {releases.map((r) => (
                <option key={r.version} value={r.version}>
                  {r.version}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="push-previous">
              Release precedente *
            </label>
            <select
              id="push-previous"
              className={styles.formSelect}
              value={previousVersion}
              onChange={(e) => setPreviousVersion(e.target.value)}
              required
              data-testid="select-push-previous"
            >
              <option value="">Seleziona...</option>
              {releases.map((r) => (
                <option key={r.version} value={r.version}>
                  {r.version}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-push-release"
          >
            {isPending ? 'Push in corso...' : 'Esegui push'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-push-release">
              {messaggioErrore()}
            </span>
          )}
          {isSuccess && (
            <span
              className={styles.inlineMutError}
              style={{ color: 'green' }}
              data-testid="successo-push-release"
            >
              Push completato.
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principale: DeploymentsSection
// ---------------------------------------------------------------------------

/** Sezione "Forniture" del dettaglio prodotto. */
export function DeploymentsSection({ productId }: { productId: string }) {
  const { can } = usePermissions();
  const [soloCorrente, setSoloCorrente] = useState(false);

  const { data: deployments, isLoading, isError, error } = useDeployments(productId, soloCorrente);
  const deleteDeployment = useDeleteDeployment(productId);

  // Carica le release per poter mostrare dtProjectUuid nelle righe
  const { data: releases = [] } = useReleases(productId);
  const releaseByVersion = Object.fromEntries(releases.map((r) => [r.version, r]));

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  return (
    <div data-testid="sezione-forniture">
      {/* Toggle solo correnti */}
      <div className={styles.formRow} style={{ marginBottom: '1rem', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={soloCorrente}
            onChange={(e) => setSoloCorrente(e.target.checked)}
            data-testid="toggle-solo-correnti"
          />
          Solo correnti
        </label>
      </div>

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-forniture">
          Caricamento forniture...
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-forniture">
          {error instanceof Error ? error.message : 'Errore nel caricamento delle forniture'}
        </p>
      )}

      {!isLoading && !isError && deployments !== undefined && deployments.length === 0 && (
        <div className={styles.emptyState} data-testid="forniture-vuote">
          Nessuna consegna registrata per questo prodotto.
        </div>
      )}

      {!isLoading && !isError && deployments !== undefined && deployments.length > 0 && (
        <table className={styles.table} data-testid="tabella-forniture">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Sito</th>
              <th>Release</th>
              <th>Label</th>
              <th>Finding</th>
              <th>Data consegna</th>
              <th>Corrente</th>
              {puoGestire && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {deployments.map((dep) => {
              const release = releaseByVersion[dep.releaseVersion];
              return (
                <tr key={dep.id} data-testid={`fornitura-row-${dep.id}`}>
                  <td>{dep.customerId}</td>
                  <td>{dep.siteId ?? '-'}</td>
                  <td>
                    <span className={styles.badge}>{dep.releaseVersion}</span>
                  </td>
                  <td>{dep.label ?? '-'}</td>
                  <td data-testid={`finding-count-${dep.id}`}>
                    <FindingCount dtProjectUuid={release?.dtProjectUuid} />
                  </td>
                  <td>{dep.deliveredAt}</td>
                  <td>
                    {dep.isCurrent ? (
                      <span className={styles.badge}>si</span>
                    ) : (
                      <span style={{ color: '#999' }}>no</span>
                    )}
                  </td>
                  {puoGestire && (
                    <td>
                      <button
                        className={`${styles.btnDanger} ${styles.btnSmall}`}
                        onClick={() => deleteDeployment.mutate(dep.id)}
                        disabled={deleteDeployment.isPending}
                        data-testid={`btn-elimina-fornitura-${dep.id}`}
                      >
                        Elimina
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {deleteDeployment.isError && (
        <span className={styles.inlineMutError} data-testid="errore-elimina-fornitura">
          {deleteDeployment.error instanceof Error
            ? deleteDeployment.error.message
            : "Errore durante l'eliminazione della consegna."}
        </span>
      )}

      {/* Form e azioni di scrittura solo con PORTFOLIO_MANAGEMENT */}
      {puoGestire && (
        <>
          <NuovaConsegnaForm productId={productId} />
          <PushReleaseForm productId={productId} />
        </>
      )}
    </div>
  );
}
